import {
  createProjectFn,
  deleteProjectFn,
  listAllProjectsOptions,
  toggleProjectActiveFn,
  updateProjectFn,
} from "@/actions/project/handler";
import type { InsertProject } from "@/api/db/schema/project";
import { ImageSelector } from "@/components/admin/ImageSelector";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { ArrowLeft, FolderOpen, Pencil, Plus, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/projects")({
  beforeLoad: async ({ context }) => {
    if (!context.user || context.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(listAllProjectsOptions());
  },
  component: RouteComponent,
});

function RouteComponent() {
  const router = useRouter();
  const { data } = useSuspenseQuery(listAllProjectsOptions());
  const projects = (data as any)?.body?.projects || [];

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [formData, setFormData] = useState<
    Omit<
      InsertProject,
      "projectId" | "createdAt" | "updatedAt" | "deletedAt" | "isActive"
    >
  >({
    title: "",
    subtitle: "",
    description: "",
    icon: "",
    features: [] as string[],
    displayOrder: 0,
    imageUrl: "" as string | null,
  });

  const resetForm = () => {
    setFormData({
      title: "",
      subtitle: "",
      description: "",
      icon: "",
      features: [] as string[],
      displayOrder: 0,
      imageUrl: "",
    });
    setEditingId(null);
  };

  const createMutation = useMutation({
    mutationFn: (data: InsertProject) => createProjectFn({ data }),
    onSuccess: () => {
      toast.success("Proje oluşturuldu");
      setIsCreateOpen(false);
      resetForm();
      router.invalidate();
    },
    onError: () => toast.error("Proje oluşturulamadı"),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; data: Partial<InsertProject> }) =>
      updateProjectFn({ data: { projectId: vars.id, ...vars.data } }),
    onSuccess: () => {
      toast.success("Proje güncellendi");
      setIsCreateOpen(false);
      resetForm();
      router.invalidate();
    },
    onError: () => toast.error("Proje güncellenemedi"),
  });

  const deleteMutation = useMutation({
    mutationFn: (projectId: string) => deleteProjectFn({ data: { projectId } }),
    onSuccess: () => {
      toast.success("Proje silindi");
      router.invalidate();
    },
    onError: () => toast.error("Proje silinemedi"),
  });

  const toggleMutation = useMutation({
    mutationFn: (projectId: string) =>
      toggleProjectActiveFn({ data: { projectId } }),
    onSuccess: () => {
      toast.success("Durum güncellendi");
      router.invalidate();
    },
    onError: () => toast.error("Durum güncellenemedi"),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    } else {
      createMutation.mutate(formData as InsertProject);
    }
  };

  const handleEdit = (project: any) => {
    setFormData({
      title: project.title,
      subtitle: project.subtitle || "",
      description: project.description,
      icon: project.icon || "",
      features: Array.isArray(project.features)
        ? project.features
        : typeof project.features === "string"
          ? JSON.parse(project.features || "[]")
          : [],
      displayOrder: project.displayOrder,
      imageUrl: project.imageUrl || "",
    });
    setEditingId(project.projectId);
    setIsCreateOpen(true);
  };

  return (
    <div className="container mx-auto px-4 py-24 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 mb-2 text-muted-foreground"
            onClick={() => router.history.back()}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Geri Dön
          </Button>
          <div className="flex items-center gap-2">
            <FolderOpen className="h-8 w-8 text-primary" />
            <h1 className="font-bold text-2xl sm:text-3xl">Proje Yönetimi</h1>
          </div>
          <p className="text-muted-foreground text-sm sm:text-base">
            Platformdaki projeleri oluşturun ve düzenleyin
          </p>
        </div>

        <Dialog
          open={isCreateOpen}
          onOpenChange={(open) => {
            setIsCreateOpen(open);
            if (!open) resetForm();
          }}
        >
          <DialogTrigger asChild>
            <Button className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              Yeni Proje
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingId ? "Projeyi Düzenle" : "Yeni Proje Oluştur"}
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={handleSubmit}
              className="max-h-[80vh] auto-rows-max space-y-4 overflow-y-auto pt-4"
            >
              <div className="space-y-2">
                <Label htmlFor="title">Başlık</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, title: e.target.value }))
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subtitle">Alt Başlık (Rozet vb.)</Label>
                <Input
                  id="subtitle"
                  value={formData.subtitle || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      subtitle: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="icon">
                  İkon Adı (Lucide - Örn: GraduationCap)
                </Label>
                <Input
                  id="icon"
                  value={formData.icon || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      icon: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <ImageSelector
                  label="Proje Görseli"
                  value={formData.imageUrl}
                  onChange={(url) =>
                    setFormData((prev) => ({ ...prev, imageUrl: url }))
                  }
                  description="İkon yerine proje kapak görseli koysan daha iyi olur dedin :)"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Özellikler</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        features: [...(prev.features as string[]), ""],
                      }))
                    }
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Özellik Ekle
                  </Button>
                </div>
                <div className="space-y-2">
                  {(formData.features as string[]).map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={feature}
                        onChange={(e) => {
                          const newFeatures = [
                            ...(formData.features as string[]),
                          ];
                          newFeatures[index] = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            features: newFeatures,
                          }));
                        }}
                        placeholder={`Özellik ${index + 1}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-10 text-muted-foreground hover:text-destructive"
                        onClick={() => {
                          const newFeatures = [
                            ...(formData.features as string[]),
                          ];
                          newFeatures.splice(index, 1);
                          setFormData((prev) => ({
                            ...prev,
                            features: newFeatures,
                          }));
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {(formData.features as string[]).length === 0 && (
                    <p className="py-2 text-center text-muted-foreground text-xs italic">
                      Henüz özellik eklenmedi
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="displayOrder">
                  Sıralama (Büyük olan üstte çıkar)
                </Label>
                <Input
                  id="displayOrder"
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      displayOrder: Number.parseInt(e.target.value) || 0,
                    }))
                  }
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Kaydediliyor..."
                  : "Kaydet"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((project: any) => (
          <Card key={project.projectId} className="flex flex-col">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <CardTitle className="line-clamp-2 text-lg">
                  {project.title}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-muted-foreground text-xs">
                    Öncelik: {project.displayOrder}
                  </span>
                  <Switch
                    checked={project.isActive}
                    onCheckedChange={() =>
                      toggleMutation.mutate(project.projectId)
                    }
                  />
                </div>
              </div>
              {project.subtitle && (
                <p className="line-clamp-1 text-muted-foreground text-sm">
                  {project.subtitle}
                </p>
              )}
            </CardHeader>
            <CardContent className="flex flex-1 flex-col justify-end">
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(project)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    if (
                      confirm("Bu projeyi silmek istediğinize emin misiniz?")
                    ) {
                      deleteMutation.mutate(project.projectId);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {projects.length === 0 && (
          <div className="col-span-full rounded-lg border border-dashed p-8 text-center">
            <p className="text-muted-foreground">Henüz proje eklenmemiş</p>
          </div>
        )}
      </div>
    </div>
  );
}
